/**
 * The text creation flow's pure rules (ADR-115; handoff §5, §6, §16).
 *
 * Everything here is a function of what the server returned (the analysis, the
 * budget check) and what the learner chose. No number, limit or refresh time is
 * defined here: counts are counted, the rest is read from the server.
 */
import type {
  PlannedCardWord,
  TextAnalysis,
  TextBudgetCheck,
  TextDestinationKind,
  WordCandidateResponse,
} from '../types'

// ── Groups of the list view (handoff §5, with Stanley's sixth group) ──────────

/** «Отмеченные» is client state; the other five are where the server put the word. */
export type ListGroup = 'marked' | 'studying' | 'other' | 'known' | 'function' | 'names'

/** «Уже изучаете» sits right after «Отмеченные», styled like the others. */
export const GROUP_ORDER: readonly ListGroup[] = [
  'marked',
  'studying',
  'other',
  'known',
  'function',
  'names',
]

type ComputedGroup = Exclude<ListGroup, 'marked'>

export interface ListWord {
  lemma: string
  /** Its card identity: the mark it shares with the word in the text (`textMarks.wordKey`). */
  key: string
  /** What the chip reads, and so what the card's Term will be. */
  label: string
  /** The group the server put the word in, whatever its mark. */
  computed: ComputedGroup
  /** Position of its first occurrence: text order. */
  position: number
}

export interface LemmaSpan {
  start: number
  end: number
  /** The word as written in the body. */
  text: string
  /** The word is exactly this one lemma (not a compound such as "well-known"). */
  standalone: boolean
}

/**
 * The span a lemma's card mark covers: its first standalone occurrence, else the
 * first word that contains it. A hyphenated compound is one word (ADR-115, the
 * gap matcher's boundaries), so a lemma met only inside one is carded as the
 * compound itself, never as its part.
 */
export function lemmaSpans(analysis: TextAnalysis): Map<string, LemmaSpan> {
  const spans = new Map<string, LemmaSpan>()
  for (const w of analysis.words) {
    for (const lemma of w.lemmas) {
      const standalone = w.lemmas.length === 1
      const known = spans.get(lemma)
      if (known && (known.standalone || !standalone)) continue
      spans.set(lemma, { start: w.start, end: w.end, text: w.text, standalone })
    }
  }
  return spans
}

/**
 * A lemma's card key, as the server de-duplicates cards: the lemma itself, or the
 * compound it is met in when it never stands alone (`textMarks.wordKey` agrees).
 */
export function lemmaKey(spans: Map<string, LemmaSpan>, lemma: string): string {
  const span = spans.get(lemma)
  return span && !span.standalone ? `w:${span.text.toLowerCase()}` : `l:${lemma}`
}

/** Every distinct word of the text, once, in text order, with its server group. */
export function listWords(analysis: TextAnalysis): ListWord[] {
  const spans = lemmaSpans(analysis)
  const entries: [ComputedGroup, WordCandidateResponse[]][] = [
    ['other', analysis.candidates],
    ['studying', analysis.groups.already_studying],
    ['known', analysis.groups.already_known],
    ['function', analysis.groups.function_words],
    ['names', analysis.groups.names],
  ]
  const seen = new Set<string>()
  const words: ListWord[] = []
  for (const [computed, items] of entries) {
    for (const item of items) {
      if (seen.has(item.lemma)) continue
      seen.add(item.lemma)
      const span = spans.get(item.lemma)
      words.push({
        lemma: item.lemma,
        key: lemmaKey(spans, item.lemma),
        label: span && !span.standalone ? span.text : item.word,
        computed,
        position: span?.start ?? Number.MAX_SAFE_INTEGER,
      })
    }
  }
  return words.sort((a, b) => a.position - b.position)
}

/** The server's pre-selection: candidates above the learner's level that are not known. */
export function initialMarked(analysis: TextAnalysis): string[] {
  return analysis.candidates.filter((c) => c.preselected).map((c) => c.lemma)
}

/**
 * Each distinct word in exactly one group; the groups add up to the distinct count.
 * A word is in «Отмеченные» when its card key carries a role (card, gap or both).
 */
export function groupWords(
  analysis: TextAnalysis,
  markedKeys: ReadonlySet<string>,
): Record<ListGroup, ListWord[]> {
  const groups: Record<ListGroup, ListWord[]> = {
    marked: [], studying: [], other: [], known: [], function: [], names: [],
  }
  for (const word of listWords(analysis)) {
    groups[markedKeys.has(word.key) ? 'marked' : word.computed].push(word)
  }
  return groups
}

// ── Step 3 ────────────────────────────────────────────────────────────────────

const TITLE_MAX_CHARS = 48

/**
 * The default title: the first sentence, cut at a word with «…» when long (§6.1). Leading
 * punctuation and spaces are skipped, so a text pasted from the middle of a sentence
 * («, when they were rested…») is titled from its first word.
 */
export function defaultTitle(body: string): string {
  const flat = body.replace(/\s+/g, ' ').replace(/^[^\p{L}\p{N}]+/u, '').trimEnd()
  const stop = flat.search(/[.!?…](\s|$)/)
  const sentence = stop === -1 ? flat : flat.slice(0, stop)
  if (sentence.length <= TITLE_MAX_CHARS) return sentence
  const cut = sentence.slice(0, TITLE_MAX_CHARS)
  const atWord = cut.lastIndexOf(' ')
  return `${(atWord > 16 ? cut.slice(0, atWord) : cut).trimEnd()}…`
}

/**
 * «Без колоды» rules (§6.2). With a card mark the destination must be a deck, and
 * «Без колоды» is unavailable; with none it is the default.
 */
export function destinationRules(cardCount: number): {
  noDeckAvailable: boolean
  defaultKind: TextDestinationKind
} {
  return cardCount > 0
    ? { noDeckAvailable: false, defaultKind: 'new_deck' }
    : { noDeckAvailable: true, defaultKind: 'none' }
}

/** What a chosen destination becomes once the marks are known: never «Без колоды» with cards. */
export function effectiveDestination(
  chosen: TextDestinationKind | null,
  cardCount: number,
  hasDeck: boolean,
): TextDestinationKind {
  const { defaultKind } = destinationRules(cardCount)
  if (chosen === null) return defaultKind
  if (cardCount > 0 && chosen === 'none') return 'new_deck'
  if (chosen === 'existing_deck' && !hasDeck) return cardCount > 0 ? 'new_deck' : 'none'
  return chosen
}

/** «Сохранить текст»: free when no gaps; the entry decides its default (§3, §6.1). */
export function defaultSaveText(entry: 'words' | 'essay'): boolean {
  return entry === 'essay'
}

// ── Refresh time (§16) ────────────────────────────────────────────────────────

/** API datetimes are zone-less UTC: parse them with a Z. */
export function parseUtc(iso: string): Date {
  return new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`)
}

export interface RefreshWhen {
  /** Calendar days from today, in the learner's local time. */
  daysAway: number
  /** Local time, HH:MM. */
  time: string
}

export function refreshWhen(iso: string, now: Date = new Date()): RefreshWhen {
  const at = parseUtc(iso)
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const daysAway = Math.round((startOf(at) - startOf(now)) / 86_400_000)
  const hh = String(at.getHours()).padStart(2, '0')
  const mm = String(at.getMinutes()).padStart(2, '0')
  return { daysAway, time: `${hh}:${mm}` }
}

// ── The daily word budget at step 3 (handoff §16) ─────────────────────────────


/**
 * What the server's step-3 check means for the learner, before saving. Every
 * number is the server's: which words become cards today, which wait, which the
 * deck already holds. Nothing is dropped silently.
 */
export type BudgetView =
  /** Everything fits (or nothing is marked): no panel. */
  | { kind: 'fits'; accepted: number; skipped: number }
  /** §16.1: the text is saved, the last words wait. */
  | { kind: 'waits'; accepted: number; waiting: PlannedCardWord[]; skipped: number }
  /** §16.1 «Nothing left today»: every card word waits. */
  | { kind: 'exhausted'; waiting: PlannedCardWord[]; skipped: number }
  /** The text is off and some words cannot become cards today: say so, offer to keep the text. */
  | { kind: 'cardsOnly'; accepted: number; waiting: PlannedCardWord[]; skipped: number }

export function budgetView(
  check: TextBudgetCheck | null,
  saveText: boolean,
  cardCount: number,
): BudgetView | null {
  if (cardCount === 0 || !check) return null
  const accepted = check.accepted.length
  const waiting = check.waiting
  const skipped = check.skipped.length
  if (waiting.length === 0) return { kind: 'fits', accepted, skipped }
  if (!saveText) return { kind: 'cardsOnly', accepted, waiting, skipped }
  if (accepted === 0) return { kind: 'exhausted', waiting, skipped }
  return { kind: 'waits', accepted, waiting, skipped }
}

export type SaveButtonKind =
  | { kind: 'textOnly' }
  | { kind: 'textAndCards'; n: number }
  | { kind: 'cardsOnly'; n: number }

/** The primary button's label case (§6.3, §16.1) and whether saving would do anything. */
export function saveButton(
  view: BudgetView | null,
  saveText: boolean,
  cardCount: number,
): { label: SaveButtonKind; disabled: boolean } {
  if (cardCount === 0) return { label: { kind: 'textOnly' }, disabled: false }
  // Before the check answers (or when it cannot), the marks themselves are the count.
  const n = view && 'accepted' in view ? view.accepted : view ? 0 : cardCount
  if (!saveText) {
    return { label: { kind: 'cardsOnly', n }, disabled: view !== null && n === 0 }
  }
  if (view?.kind === 'exhausted' || (view && n === 0)) return { label: { kind: 'textOnly' }, disabled: false }
  return { label: { kind: 'textAndCards', n }, disabled: false }
}
